/**
 * PoisPage - Página de POIs Premium
 * Migrado para padrão Premium Fleet
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  Stack,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import PlaceIcon from '@mui/icons-material/Place';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StarIcon from '@mui/icons-material/Star';

import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useManager } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync } from '../reactHelper';
import useCrudExports from '../common/hooks/useCrudExports';
import { poisActions } from '../store';
import PoiImportDialog from './PoiImportDialog';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTableSelection } from '../common/hooks';

// Componentes extraídos
import PoisTableRow from './components/PoisTableRow';
import {
  PremiumStatCards,
  PremiumEmptyState,
  BulkActionsBar,
  BulkDeleteDialog,
} from '../common/components/premium';

const PoisPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const manager = useManager();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState({});
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Bulk action states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const tableContainerRef = useRef(null);

  // Fetch de dados
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const [poisRes, groupsRes, iconsRes] = await Promise.all([
        fetchOrThrow('/api/pois'),
        fetchOrThrow('/api/poigroups'),
        fetchOrThrow('/api/poiicons'),
      ]);
      const poisData = await poisRes.json();
      const groupsData = await groupsRes.json();
      const iconsData = await iconsRes.json();

      setItems(poisData);
      const groupsMap = {};
      groupsData.forEach((g) => { groupsMap[g.id] = g; });
      setGroups(groupsMap);
      setIcons(iconsData);

      // Update Redux store
      dispatch(poisActions.refresh(poisData));
      dispatch(poisActions.refreshGroups(groupsData));
      dispatch(poisActions.setIcons(iconsData));
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // Estatísticas
  const stats = useMemo(() => {
    const visible = items.filter((i) => i.visible).length;
    const hidden = items.filter((i) => !i.visible).length;
    const favorites = items.filter((i) => i.favorite).length;
    const groupCount = Object.keys(groups).length;
    return { total: items.length, visible, hidden, favorites, groups: groupCount };
  }, [items, groups]);

  // Filtros para StatCards (usa tokens dinâmicos)
  const statFilters = useMemo(() => [
    { value: '', label: t('sharedAll'), count: stats.total, color: tokens?.accent?.primary, Icon: PlaceIcon },
    { value: 'visible', label: t('sharedShow'), count: stats.visible, color: tokens?.semantic?.success, Icon: VisibilityIcon },
    { value: 'hidden', label: t('sharedHide'), count: stats.hidden, color: tokens?.semantic?.warning, Icon: VisibilityOffIcon },
    { value: 'favorites', label: t('poiFavorites') || 'Favoritos', count: stats.favorites, color: tokens?.semantic?.warning, Icon: StarIcon },
  ], [t, stats, tokens]);

  // Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter === 'visible') {
      result = result.filter((i) => i.visible);
    } else if (statusFilter === 'hidden') {
      result = result.filter((i) => !i.visible);
    } else if (statusFilter === 'favorites') {
      result = result.filter((i) => i.favorite);
    }
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) => i.name?.toLowerCase().includes(lower)
          || i.description?.toLowerCase().includes(lower)
          || i.address?.toLowerCase().includes(lower)
          || i.city?.toLowerCase().includes(lower)
          || i.contactName?.toLowerCase().includes(lower)
          || i.id?.toString().includes(lower),
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Bulk selection hook
  const {
    selectedIds,
    selectedCount,
    toggleSelect,
    selectAll,
    clearSelection,
    isSelected,
    isAllSelected,
    isIndeterminate,
    hasSelection,
  } = useTableSelection(filteredItems);

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          fetchOrThrow(`/api/pois/${id}`, { method: 'DELETE' })
        )
      );
      clearSelection();
      setDeleteDialogOpen(false);
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Erro ao excluir POIs:', error);
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, clearSelection]);

  // Toggle visibility
  const handleToggleVisibility = useCallback(async (poi) => {
    try {
      await fetchOrThrow(`/api/pois/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...poi, visible: !poi.visible }),
      });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  }, []);

  // Toggle favorite
  const handleToggleFavorite = useCallback(async (poi) => {
    try {
      await fetchOrThrow(`/api/pois/${poi.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...poi, favorite: !poi.favorite }),
      });
      setTimestamp(Date.now());
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  }, []);

  // Handlers
  const handleFilterChange = useCallback((value) => {
    setStatusFilter(value);
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setSearchText('');
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // Exportações
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'name', headerNameKey: 'sharedName' },
    { field: 'description', headerNameKey: 'sharedDescription' },
    { field: 'latitude', headerNameKey: 'positionLatitude' },
    { field: 'longitude', headerNameKey: 'positionLongitude' },
    { field: 'groupName', headerNameKey: 'sharedGroup', valueGetter: ({ row }) => groups[row.groupId]?.name || '-' },
    { field: 'address', headerNameKey: 'poiAddress' },
    { field: 'city', headerNameKey: 'poiCity' },
    { field: 'state', headerNameKey: 'poiState' },
    { field: 'contactName', headerNameKey: 'poiContactName' },
    { field: 'contactPhone', headerNameKey: 'poiContactPhone' },
    { field: 'visible', headerNameKey: 'sharedShow', valueGetter: ({ row }) => (row.visible ? t('sharedYes') : t('sharedNo')) },
    { field: 'favorite', headerNameKey: 'poiFavorite', valueGetter: ({ row }) => (row.favorite ? t('sharedYes') : t('sharedNo')) },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'poiTitle',
    headerKey: 'poiTitle',
    rows: filteredItems,
    columns: exportColumns,
    t,
    options: { includeWatermark: true },
  });

  // Check if has active filters
  const hasFilters = Boolean(statusFilter || searchText.trim());

  // Header styles for premium table
  const headerCellSx = {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: tokens?.text?.secondary,
    borderBottom: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
    py: 1.5,
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'poiTitle']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Loading State */}
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <Typography variant="body1" sx={{ color: tokens?.text?.secondary }}>
                {t('sharedLoading') || 'Carregando...'}
              </Typography>
            </Box>
          ) : (
            <>
              {/* CARDS DE ESTATÍSTICAS */}
              <PremiumStatCards
                filters={statFilters}
                activeFilter={statusFilter}
                onFilterChange={handleFilterChange}
              />

              {/* BARRA DE AÇÕES */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, sm: 1.5 },
                  alignItems: { xs: 'stretch', md: 'center' },
                  flexShrink: 0,
                }}
              >
                <TextField
                  size="small"
                  value={searchText}
                  onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                  placeholder={t('sharedSearch')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: tokens?.accent?.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: { xs: '100%', sm: 200 },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: tokens?.surface?.glass,
                      borderRadius: tokens?.radius?.md,
                      '&:hover': { backgroundColor: tokens?.surface?.hover },
                    },
                  }}
                />
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ flexShrink: 0, flexWrap: 'wrap', gap: 0.5, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleIcon />}
                    onClick={() => navigate('/settings/poi')}
                    sx={{ backgroundColor: tokens?.accent?.primary, '&:hover': { backgroundColor: tokens?.accent?.hover } }}
                  >
                    {t('sharedAdd')}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<FileUploadIcon />}
                    onClick={() => setImportDialogOpen(true)}
                    sx={{ backgroundColor: tokens?.semantic?.success, '&:hover': { backgroundColor: `${tokens?.semantic?.success}dd` } }}
                  >
                    {t('poiImportButton') || 'Importar'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CategoryIcon />}
                    onClick={() => navigate('/settings/poigroups')}
                    sx={{
                      color: tokens?.text?.primary,
                      borderColor: tokens?.border?.default,
                      '&:hover': { borderColor: tokens?.accent?.primary, bgcolor: `${tokens?.accent?.primary}10` },
                    }}
                  >
                    {t('sharedGroups')}
                  </Button>
                  {manager && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadIcon />}
                      endIcon={<ExpandMoreIcon />}
                      onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                      sx={{
                        color: tokens?.text?.primary,
                        borderColor: tokens?.border?.default,
                        '&:hover': { borderColor: tokens?.accent?.primary, bgcolor: `${tokens?.accent?.primary}10` },
                      }}
                    >
                      {t('reportExport') || 'Exportar'}
                    </Button>
                  )}
                </Stack>
              </Box>

              {/* Results count & Bulk Actions Bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredItems.length} {t('poiTitle') || 'POIs'}
                  {hasFilters && ` (${t('sharedFiltered') || 'filtrado'})`}
                </Typography>

                {hasSelection && (
                  <BulkActionsBar
                    selectedCount={selectedCount}
                    onClearSelection={clearSelection}
                    onDelete={() => setDeleteDialogOpen(true)}
                    loading={bulkActionLoading}
                    entityName="POI(s)"
                  />
                )}
              </Box>

              {/* TABELA */}
              <Paper
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  borderRadius: tokens?.radius?.lg || '12px',
                  backgroundColor: tokens?.surface?.glass,
                  border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                }}
              >
                <TableContainer ref={tableContainerRef} className="premium-table-container" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ ...headerCellSx, width: 48 }}>
                          <Checkbox
                            indeterminate={isIndeterminate}
                            checked={isAllSelected}
                            onChange={selectAll}
                            sx={{
                              color: tokens?.text?.tertiary,
                              '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: tokens?.accent?.primary },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ ...headerCellSx, width: 50 }} />
                        <TableCell sx={{ ...headerCellSx, width: 40 }} />
                        <TableCell sx={headerCellSx}>{t('sharedName')}</TableCell>
                        <TableCell sx={headerCellSx}>{t('poiCity') || 'Cidade'}</TableCell>
                        <TableCell sx={headerCellSx}>{t('sharedGroup')}</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>{t('sharedShow')}</TableCell>
                        <TableCell sx={{ ...headerCellSx, textAlign: 'right' }}>{t('sharedActions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginated.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ border: 0, p: 0 }}>
                            <PremiumEmptyState
                              icon={PlaceIcon}
                              title={t('poisEmpty') || 'Nenhum POI cadastrado'}
                              titleFiltered={t('poisNotFound') || 'Nenhum POI encontrado'}
                              subtitle={t('poisEmptyHint') || 'Comece adicionando seu primeiro ponto de interesse'}
                              subtitleFiltered={t('poisNotFoundHint') || 'Tente ajustar os filtros de busca'}
                              hasFilters={hasFilters}
                              onClearFilters={handleClearFilters}
                              addPath="/settings/poi"
                              addLabel={t('poiAdd') || 'Adicionar POI'}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginated.map((row) => (
                          <PoisTableRow
                            key={row.id}
                            row={row}
                            groups={groups}
                            icons={icons}
                            onRefresh={handleRefresh}
                            onToggleVisibility={handleToggleVisibility}
                            onToggleFavorite={handleToggleFavorite}
                            selected={isSelected(row.id)}
                            onSelect={toggleSelect}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {filteredItems.length > 0 && (
                  <TablePagination
                    component="div"
                    count={filteredItems.length}
                    page={page}
                    onPageChange={(_, newPage) => { setPage(newPage); if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0; }}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage={t('rowsPerPage') || 'Linhas por página'}
                    sx={{
                      borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                        fontSize: '0.8rem',
                        color: tokens?.text?.secondary,
                      },
                    }}
                  />
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        PaperProps={{
          sx: { bgcolor: tokens?.surface?.elevated, borderRadius: tokens?.radius?.lg, border: `1px solid ${tokens?.border?.subtle}` },
        }}
      >
        <MenuItem onClick={() => { handleExportCsv(); setExportMenuAnchor(null); }} sx={{ color: tokens?.text?.primary }}>
          <FileDownloadIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.success }} />
          CSV
        </MenuItem>
        <MenuItem onClick={() => { handleExportXlsx(); setExportMenuAnchor(null); }} sx={{ color: tokens?.text?.primary }}>
          <TableChartIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.info }} />
          Excel
        </MenuItem>
        <MenuItem onClick={() => { handleExportPdf(); setExportMenuAnchor(null); }} sx={{ color: tokens?.text?.primary }}>
          <PictureAsPdfIcon sx={{ mr: 1, fontSize: 18, color: tokens?.semantic?.error }} />
          PDF
        </MenuItem>
      </Menu>

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        selectedCount={selectedCount}
        loading={bulkActionLoading}
        entityName="POI(s)"
      />

      {/* Dialog de Importação */}
      <PoiImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={() => setTimestamp(Date.now())}
      />
    </PageLayout>
  );
};

export default PoisPage;
